// V96.11 — French composer (Phase 3.B). Assembles a system prompt by
// composing :
//   1. The base SYSTEM_PROMPT_FR (identity + clinical rules + style)
//   2. The Swiss brands context
//   3. Optional supplements rules (if the client is open to them)
//   4. Profile-specific modules detected from the form (femmeCycle,
//      perimenopause, menopause, diabete, digestifChronique)
//   5. The plan-mode prompt (oneshot, fourWeeks, or followup)
//
// Compared to buildSystemPromptFr in ./fr.js, the composer adds STEP 4
// (profile injection) and is otherwise behavior-equivalent.
//
// IMPORTANT — opt-in path :
//   This composer is NOT wired into NutritionConsultation.jsx by default.
//   It must be called explicitly via buildSystemPromptFrV2 (also exported
//   from ./fr.js) with the option { useComposer: true }. Until Anissa has
//   reviewed the profile modules, the legacy path remains active.

import {
  SYSTEM_PROMPT_FR,
  SWISS_BRANDS_PROMPT_FR,
  SUPPLEMENT_PROMPT_FR,
  ONESHOT_PLAN_PROMPT_FR,
  FOUR_WEEKS_PROMPT_FR,
  buildFollowupPromptFr,
  buildSystemPromptFr,
} from './fr';
import { detectClientProfile } from './profiles/_detector.fr';
import { getProfileModuleFr } from './profiles/index.fr';
import { resolveClinicalState } from '../../clinical/resolveClinicalState';
// V97.4 (Phase V1) : couche clinicalContext — tests / markers / signals /
// microbiomeStage / modules / safety rules. Voir ./_clinicalContext.fr.js.
import { buildClinicalContextBlockFr, buildSafetyBlockFr } from './_clinicalContext.fr';
// V97.4 V3.H Gap #3 : objectifs priorisés pour focaliser le plan IA.
import { formatPrioritizedObjectivesFr } from './_objectives.fr';
// V97.x Phase 1 (urgent risque légal) : couche garde-fous cliniques.
// Phrases interdites + vocab précaution injectés en TÊTE du prompt pour
// que le LLM les voie en premier (priorité maximale).
import { detectClinicalGuardrails, buildGuardrailsBlockFr } from './_clinicalGuardrails.fr';
// V97.18 Phase D : recommandations cliniques de la phase active du parcours.
// Permet a Anissa d'editer la matrice via cockpit "📋 Phases" et que les
// generations de plans alignent automatiquement les recommandations.
import { getLivePhaseRecommendations, buildPhaseRecommendationsBlockFr } from '../../protocolPhases';

// ─── VERROU D'ACTIVATION DES MODULES PROFIL — 2026-07-31 ─────────────────
// Anissa a relu les 8 modules profil (grossesse, allaitement, menopause,
// sopk, endometriose, thyroide, diabete, digestifChronique) et n'en a
// valide AUCUN en l'etat : « Aucun module ne doit etre active a la suite
// de cette premiere relecture. Je souhaite recevoir une deuxieme version
// corrigee, puis valider separement chaque module. »
// Verbatim + corrections demandees :
//   docs/RELECTURE-MODULES-COMPOSER-ANISSA-2026-07-31.md
//
// Decouvert le meme jour : ces modules n'etaient PAS dormants comme on le
// croyait. Le composer est ON PAR DEFAUT pour les clientes creees apres le
// 2026-05-12 (COMPOSER_BETA_DEFAULT_CUTOFF dans JourneyPlanEditor.jsx), et
// c'est lui qui injecte les modules profil ci-dessous. 4 clientes vivantes
// etaient concernees. Le toggle localStorage ne suffisait donc pas de
// garde-fou : ce registre en est un vrai, cote code.
//
// REGLE : un module n'est injecte QUE si sa cle figure ici.
// N'ajouter une cle qu'avec une validation ECRITE d'Anissa, et referencer le
// document ou elle l'a donnee. Fail-closed : dans le doute, on n'injecte pas.
//
// ─── Registre des validations ───────────────────────────────────────────
// 'menopause' — VALIDE PAR ECRIT le 2026-07-31 : « Je valide formellement
//   le module Menopause V2 tel qu'il est redige. C'est actuellement le seul
//   module pouvant etre ajoute a MODULES_PROFIL_VALIDES_ANISSA. Aucun autre
//   module ne doit etre active. »
//   Source : docs/VALIDATION-MODULES-ANISSA-V2-2026-07-31.md
//
// Les 7 autres modules restent VERROUILLES sur sa decision explicite.
export const MODULES_PROFIL_VALIDES_ANISSA = ['menopause'];

/**
 * Build the FR system prompt with profile-aware composition.
 *
 * V97.4 (Phase V1) : ajout d'un 3e paramètre optionnel `clinicalContext`
 * pour injecter le bloc clinique structuré (tests / markers / résultats /
 * signaux / microbiomeStage) entre les profileModules et le planMode.
 * Si `clinicalContext` est null ou vide, le comportement reste identique
 * à la version V96.11 (rétro-compatibilité garantie).
 *
 * @param {object} form - The client anamnese (clients.form in storage)
 * @param {object} opts - Plan options
 * @param {boolean} [opts.isFollowup]   - true for weekly follow-up
 * @param {string}  [opts.clientFormule] - 'suivi' | 'intensif' | 'autonome' | 'nutrition' | 'custom'
 * @param {number}  [opts.followupWeek] - ordinal within the confirmed pack
 * @param {number}  [opts.followupMaximum] - confirmed administrative pack capacity
 * @param {string}  [opts.planMode]     - 'oneshot' | 'followup'
 * @param {object | null} [clinicalContext] - V97.4 : tests / markers /
 *   enteredResults / clinicalSignals / microbiomeStage / promptModules /
 *   safetyRules. Voir _clinicalContext.fr.js pour le shape complet.
 * @returns {{ prompt: string, profile: object, blocked: boolean }}
 *   - prompt   : the assembled system prompt (or empty string if blocked)
 *   - profile  : the detected client profile (for logging / UI feedback)
 *   - blocked  : true if a primary profile blocks generation (eg pregnancy
 *                without a dedicated module). Caller must surface this to
 *                the user and skip the AI call.
 */
export function composeSystemPromptFr(form, opts = {}, clinicalContext = null) {
  const profile = detectClientProfile(form);

  // 2026-07-31 — resolution clinique structuree, SOURCE UNIQUE partagee par
  // les trois chemins de generation (tous passent par ici). Elle ne bloque
  // rien a ce stade : elle DECRIT. Les blocages valides par Anissa
  // s'appuieront dessus, et le fait qu'elle soit calculee ici garantit
  // qu'aucun chemin — y compris la transition de phase automatique — ne
  // puisse diverger ou la contourner.
  const clinicalState = resolveClinicalState(form);

  // Safety gate — block generation when we lack a safe module.
  // 2026-07-31 : le verrou clinique s'ajoute ici aussi, pour que les appels
  // DIRECTS a composeSystemPromptFr (tests, outillage) soient couverts comme
  // ceux qui passent par buildSystemPromptFrV2.
  if (profile.blocked || clinicalState.bloque) {
    return {
      prompt: '',
      profile,
      blocked: true,
      clinicalState,
      blocages: clinicalState.blocages,
      motifs: clinicalState.motifs,
    };
  }

  const {
    isFollowup = false, clientFormule = '', followupWeek = 0, followupMaximum = 4, planMode = 'followup',
    // V97.18 Phase D : context de la phase active du parcours (optionnel).
    // Shape : { templateKey: string, phaseId: string, weekNumber?: number }
    activePhase = null,
  } = opts;
  const parts = [SYSTEM_PROMPT_FR, SWISS_BRANDS_PROMPT_FR];

  // P0.1 + P0.2 (remède sécurité clinique, 2026-06-10) — bloc sécurité partagé.
  // Source unique buildSafetyBlockFr(form), dérivée de form (pas de
  // clinicalContext) pour être présente même quand clinicalContext est null
  // (cas NutritionConsultation.jsx:104). Injecté juste après l'identité, avant
  // les guardrails, pour priorité LLM maximale.
  const safetyBlock = buildSafetyBlockFr(form);
  if (safetyBlock) parts.push(safetyBlock);

  // V97.x Phase 1 — Garde-fous cliniques (urgent risque légal).
  // Injectés IMMÉDIATEMENT après l'identité pour priorité LLM maximale.
  // Détecte profil → liste phrases interdites + vocab précaution.
  // Cf _clinicalGuardrails.fr.js + spec composer-v97-clinical-antislop.
  const guardrails = detectClinicalGuardrails(profile, form);
  const guardrailsBlock = buildGuardrailsBlockFr(guardrails);
  if (guardrailsBlock) {
    parts.push(guardrailsBlock);
  }

  // V97.18 Phase D — Recommandations de la phase active du parcours.
  // Source : cache DB (cockpit "📋 Phases") avec fallback hardcode JS.
  // Le LLM aligne le plan sur foods_favor/foods_limit/cooking/supplements.
  let phaseReco = null;
  if (activePhase?.templateKey && activePhase?.phaseId) {
    phaseReco = getLivePhaseRecommendations(activePhase.templateKey, activePhase.phaseId);
    const phaseBlock = buildPhaseRecommendationsBlockFr(phaseReco, {
      weekNumber: activePhase.weekNumber,
    });
    if (phaseBlock) parts.push(phaseBlock);
  }

  // Supplements gate (unchanged from legacy path).
  const pretProtocole = form?.pretProtocole || '';
  if (pretProtocole === 'Oui' || pretProtocole === 'Peut-etre') {
    parts.push(SUPPLEMENT_PROMPT_FR);
  }

  // Inject profile modules (primary first, then pathologies). V96.17 : cap
  // monte de 5 a 6, V96.26 : cap monte de 6 a 8 pour absorber les profils
  // ultra-comorbides apres ajout des 8 nouveaux modules (performanceSportif,
  // thyroide, burnoutCortisol, preConceptionFertilite, spm, endometriose, tdah,
  // sopk). Coût token marginal au regard du benefice clinique.
  // Verrou 2026-07-31 (cf. MODULES_PROFIL_VALIDES_ANISSA en tete de fichier) :
  // le filtre precede volontairement le cap a 8 — sinon un module non valide
  // consommerait une place dans le cap.
  const profileModules = profile.all
    .filter((key) => MODULES_PROFIL_VALIDES_ANISSA.includes(key))
    .slice(0, 8)
    .map(getProfileModuleFr)
    .filter(Boolean);
  if (profileModules.length > 0) {
    parts.push('// ═══ MODULES PROFIL CLIENT ═══');
    parts.push(...profileModules);
  }

  // V97.4 (Phase V1) : injection du contexte clinique structuré APRÈS les
  // profileModules et AVANT le planMode. Si clinicalContext est null ou
  // vide, buildClinicalContextBlockFr retourne '' et rien n'est injecté.
  const clinicalBlock = buildClinicalContextBlockFr(clinicalContext);
  if (clinicalBlock) {
    parts.push('// ═══ CONTEXTE CLINIQUE STRUCTURÉ ═══');
    parts.push(clinicalBlock);
  }

  // V97.4 V3.H Gap #3 : objectifs priorisés (focus plan IA).
  // Injecté APRÈS le contexte clinique → l'IA voit d'abord la situation
  // bio, puis ce qu'on veut prioriser. Retourne '' si rien rempli.
  const objectivesBlock = formatPrioritizedObjectivesFr(form);
  if (objectivesBlock) {
    parts.push('// ═══ OBJECTIFS PRIORISÉS ═══');
    parts.push(objectivesBlock);
  }

  // Plan mode (unchanged from legacy path).
  if (isFollowup && followupWeek > 0) {
    parts.push(buildFollowupPromptFr(followupWeek, followupMaximum));
  } else if (planMode === 'oneshot') {
    parts.push(ONESHOT_PLAN_PROMPT_FR);
  } else {
    const recurrentFormules = ['suivi', 'intensif', 'autonome', 'nutrition', 'custom'];
    const normalizedFormule = (clientFormule || '').trim().toLowerCase();
    if (recurrentFormules.includes(normalizedFormule)) {
      parts.push(FOUR_WEEKS_PROMPT_FR);
    }
  }

  return {
    prompt: parts.join('\n\n'),
    profile,
    blocked: false,
    clinicalState,
    // V97.x Phase 1 — Garde-fous exposés au caller pour audit post-génération.
    // Le caller (NutritionConsultation / JourneyPlanEditor) peut appeler
    // auditPlanForGuardrails(plan, guardrails) après réception du draft IA
    // pour vérifier qu'aucune phrase interdite n'est passée.
    guardrails,
    // V97.18 Phase D — Recommandations de la phase active exposees pour
    // observability + UI. Null si pas de phase active fournie.
    phaseRecommendations: phaseReco,
  };
}

// ─── V2 BUILDER (OPT-IN COMPOSER) ───
// V96.11 Phase 3.B : builder qui peut s'appuyer sur le composer pour injecter
// des modules profil clinique. Comportement par defaut INCHANGE : sans option
// useComposer, on delegue au builder historique buildSystemPromptFr.
//
// V97.4 (Phase V1) : ajout du passage `options.clinicalContext` vers le
// composer pour injecter le bloc clinique structuré (tests / markers /
// résultats / signaux / microbiomeStage). Compatibilité ascendante :
// sans clinicalContext, le composer se comporte exactement comme V96.11.
//
// V97.58 : DÉPLACÉ de fr.js vers ici pour casser le cycle d'imports
// fr.js ↔ composer.fr.js (TDZ ReferenceError intermittente en suite de tests
// complète — le dispatcher du composer appartient au composer). fr.js
// n'importe plus rien de ce fichier.
//
// Usage cote consommateur :
//   buildSystemPromptFrV2(form, opts);                       // legacy (= buildSystemPromptFr)
//   buildSystemPromptFrV2(form, opts, { useComposer: true }); // composer sans clinicalContext
//   buildSystemPromptFrV2(form, opts, { useComposer: true, clinicalContext: {...} }); // composer + contexte clinique
export function buildSystemPromptFrV2(form, opts = {}, options = {}) {
  // ─── VERROU CLINIQUE (2026-07-31) ────────────────────────────────────
  // Placé AVANT toute branche, volontairement : la branche legacy
  // (useComposer absent) renvoyait `blocked: false` sans condition, ce qui
  // en faisait un contournement complet du blocage. Les trois chemins de
  // génération — JourneyPlanEditor, NutritionConsultation,
  // autoGeneratePlanForPhaseTransition — passent tous ici, quel que soit
  // l'état du composer.
  //
  // Les contraintes maternelles de clinicalMaternalSafety.js restent en
  // place plus bas : ce verrou s'y ajoute, il ne les remplace pas.
  const clinicalState = resolveClinicalState(form);
  if (clinicalState.bloque) {
    return {
      prompt: null,
      profile: null,
      blocked: true,
      clinicalState,
      blocages: clinicalState.blocages,
      motifs: clinicalState.motifs,
    };
  }

  if (options.useComposer) {
    // V97.x Phase 2 fix : pass-through guardrails (audit post-gen).
    // V97.18 Phase D : pass-through phaseRecommendations (observability + UI).
    const composerResult = composeSystemPromptFr(
      form,
      opts,
      options.clinicalContext || null,
    );
    if (composerResult.blocked) {
      // Caller must check this before calling the AI. We return null for
      // the prompt so any naive consumer fails loudly instead of sending
      // a generic plan to a high-risk client.
      return {
        prompt: null,
        profile: composerResult.profile,
        blocked: true,
        clinicalState: composerResult.clinicalState,
      };
    }
    return {
      prompt: composerResult.prompt,
      profile: composerResult.profile,
      blocked: false,
      guardrails: composerResult.guardrails || [],
      phaseRecommendations: composerResult.phaseRecommendations || null,
      // 2026-07-31 — meme resolution clinique pour TOUS les chemins qui
      // passent par ce wrapper (editeur de parcours, consultation,
      // transition de phase automatique).
      clinicalState: composerResult.clinicalState,
    };
  }
  return {
    prompt: buildSystemPromptFr(form, opts),
    profile: null,
    blocked: false,
  };
}
